import { useMemo } from 'react';
import { categoryLabel } from '../lib/assetCategories';
import { colorForAllocationRow } from '../lib/allocationColors';
import { isAssetCategory } from '../lib/assetRegistryNormalize';
import type {
  AssetCategory,
  BankAccount,
  FinancialItem,
  InvestmentCompany,
  NetWorthSnapshot,
  RealEstateProperty,
} from '../types';
import {
  totalBankCash,
  totalInvestmentPortfolio,
  totalRealEstateValue,
} from '../utils/calculations';
import { formatEuro, formatPercentPoints } from '../utils/formatters';
import AllocationTrendChart from './AllocationTrendChart';
import styles from './NetWorthBreakdown.module.css';

export type NetWorthDomainScope = 'combined' | 'personal' | 'business';

export type AllocationRow = {
  id: string;
  label: string;
  amount: number;
};

type NetWorthBreakdownProps = {
  financialItems: FinancialItem[];
  companies: InvestmentCompany[];
  bankAccounts: BankAccount[];
  realEstateItems: RealEstateProperty[];
  domain: NetWorthDomainScope;
  history?: NetWorthSnapshot[];
  historyLoading?: boolean;
  historyError?: string | null;
};

function itemDomain(item: FinancialItem): 'personal' | 'business' {
  return item.domain === 'business' ? 'business' : 'personal';
}

function filterItemsByDomain(items: FinancialItem[], domain: NetWorthDomainScope): FinancialItem[] {
  if (domain === 'combined') return items;
  return items.filter((item) => itemDomain(item) === domain);
}

function categoryForItem(item: FinancialItem): AssetCategory {
  if (isAssetCategory(item.category)) return item.category;
  return 'other_asset';
}

export function computeAllocationRows(input: {
  financialItems: FinancialItem[];
  companies: InvestmentCompany[];
  bankAccounts: BankAccount[];
  realEstateItems: RealEstateProperty[];
  domain: NetWorthDomainScope;
}): AllocationRow[] {
  const scopedItems = filterItemsByDomain(input.financialItems, input.domain);
  const categoryTotals = new Map<AssetCategory, number>();

  for (const item of scopedItems) {
    if (item.type !== 'asset' || item.currentValue <= 0) continue;
    const category = categoryForItem(item);
    categoryTotals.set(category, (categoryTotals.get(category) ?? 0) + item.currentValue);
  }

  // Real-estate holdings are personal wealth: include their gross market value
  // in the `real_estate` bucket for the personal and combined scopes.
  if (input.domain !== 'business') {
    const realEstateValue = totalRealEstateValue(input.realEstateItems);
    if (realEstateValue > 0) {
      categoryTotals.set(
        'real_estate',
        (categoryTotals.get('real_estate') ?? 0) + realEstateValue
      );
    }
  }

  const rows: AllocationRow[] = Array.from(categoryTotals.entries())
    .filter(([, amount]) => amount > 0)
    .map(([category, amount]) => ({
      id: category,
      label: categoryLabel(category),
      amount,
    }));

  if (input.domain !== 'personal') {
    const portfolio = totalInvestmentPortfolio(input.companies);
    if (portfolio > 0) {
      rows.push({ id: 'portfolio', label: 'Portfolio', amount: portfolio });
    }

    const bankCash = totalBankCash(input.bankAccounts);
    if (bankCash > 0) {
      rows.push({ id: 'bank_cash', label: 'Bank cash', amount: bankCash });
    }
  }

  return rows.sort((a, b) => b.amount - a.amount);
}

type AllocationSegment = AllocationRow & {
  color: string;
  share: number;
};

function buildAllocationSegments(rows: AllocationRow[], grossAssets: number): AllocationSegment[] {
  return rows.map((row, index) => ({
    ...row,
    color: colorForAllocationRow(row.id, index),
    share: grossAssets > 0 ? (row.amount / grossAssets) * 100 : 0,
  }));
}

function AllocationChart({ rows, grossAssets }: { rows: AllocationRow[]; grossAssets: number }) {
  const segments = buildAllocationSegments(rows, grossAssets);
  const chartSummary = segments
    .map((segment) => `${segment.label} ${formatPercentPoints(segment.share)}`)
    .join(', ');

  return (
    <figure className={styles.chart} aria-label={`Asset allocation: ${chartSummary}`}>
      <div className={styles.chartVisual}>
        <div className={styles.chartPie} aria-hidden="true">
          <div
            className={styles.chartPieRing}
            style={{
              background: `conic-gradient(${segments
                .reduce<{ stops: string[]; cursor: number }>(
                  (acc, segment) => {
                    const start = acc.cursor;
                    const end = start + segment.share;
                    acc.stops.push(`${segment.color} ${start}% ${end}%`);
                    acc.cursor = end;
                    return acc;
                  },
                  { stops: [], cursor: 0 },
                )
                .stops.join(', ')})`,
            }}
          />
        </div>
      </div>

      <ul className={styles.legend} aria-label="Allocation breakdown">
        {segments.map((segment) => (
          <li key={segment.id} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ backgroundColor: segment.color }}
              aria-hidden="true"
            />
            <span className={styles.legendLabel}>{segment.label}</span>
            <span className={styles.legendAmount}>{formatEuro(segment.amount)}</span>
            <span className={styles.legendPercent}>{formatPercentPoints(segment.share)}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

export default function NetWorthBreakdown({
  financialItems,
  companies,
  bankAccounts,
  realEstateItems,
  domain,
  history = [],
  historyLoading = false,
  historyError = null,
}: NetWorthBreakdownProps) {
  const { allRows, grossAssets } = useMemo(() => {
    const rows = computeAllocationRows({
      financialItems,
      companies,
      bankAccounts,
      realEstateItems,
      domain,
    });
    const gross = rows.reduce((sum, row) => sum + row.amount, 0);
    return {
      allRows: rows,
      grossAssets: gross,
    };
  }, [financialItems, companies, bankAccounts, realEstateItems, domain]);

  const hasAnySection = grossAssets > 0;

  return (
    <section className={styles.section} aria-label="Asset allocation">
      {!hasAnySection ? (
        <p className={styles.emptyState}>
          No assets to allocate yet — add registry items, connect a bank, or track investments.
        </p>
      ) : (
        <div className={styles.allocationLayout}>
          <div className={styles.panel}>
            <AllocationChart rows={allRows} grossAssets={grossAssets} />
          </div>
          <div className={styles.panel}>
            <h3 className={styles.panelTitle}>Allocation over time</h3>
            <p className={styles.panelSubtitle}>Daily value per asset type.</p>
            <AllocationTrendChart
              snapshots={history}
              loading={historyLoading}
              error={historyError}
            />
          </div>
        </div>
      )}
    </section>
  );
}
